import { Link } from "react-router-dom";
import BatterySelector from "../components/BatterySelector";
import LeadForm from "../components/LeadForm";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import {
  Zap, Truck, Wrench, ShieldCheck, Sparkles, Sun, Battery, Cable, Droplet,
  Phone, MessageCircle, MapPin, Clock, ArrowRight, CheckCircle2,
} from "lucide-react";
import { useState } from "react";

const IMG = {
  hero: "https://customer-assets-jt897jd0.emergentagent.net/job_battery-finder-5/artifacts/urmlm4sj_hero1.png",
  batteryLineup: "https://customer-assets-jt897jd0.emergentagent.net/job_battery-finder-5/artifacts/n7v5tmdk_batteries%26hups.JPG",
  lubricants: "https://customer-assets-jt897jd0.emergentagent.net/job_battery-finder-5/artifacts/mxbzmm17_lubricant.webp",
  hups: "https://customer-assets-jt897jd0.emergentagent.net/job_battery-finder-5/artifacts/iefel4bi_hups1.png",
  hupsBatteries: "https://customer-assets-jt897jd0.emergentagent.net/job_battery-finder-5/artifacts/3t8bhvgq_hups.jpg",
  storeInside: "https://customer-assets-jt897jd0.emergentagent.net/job_battery-finder-5/artifacts/p5ci45jp_storeinside1.webp",
  bike: "https://images.pexels.com/photos/37638824/pexels-photo-37638824.jpeg",
  solar: "https://customer-assets-jt897jd0.emergentagent.net/job_battery-finder-5/artifacts/bq250s98_solar.webp",
  inverter: "https://customer-assets-jt897jd0.emergentagent.net/job_battery-finder-5/artifacts/iefel4bi_hups1.png",
  battery: "https://customer-assets-jt897jd0.emergentagent.net/job_battery-finder-5/artifacts/n7v5tmdk_batteries%26hups.JPG",
  truck: "https://images.pexels.com/photos/37169749/pexels-photo-37169749.jpeg",
};

const CATEGORIES = [
  { id: "batteries", title: "Batteries", desc: "Batteries for Car, Bike, Taxi, Truck, Tractor and Construction / SPV — starting power that lasts.", img: IMG.batteryLineup, icon: Battery, span: "md:col-span-2 md:row-span-2" },
  { id: "hups", title: "HUPS", desc: "Amaron Pro Hi-Backup UPS — pure sinewave, pure peace.", img: IMG.hups, icon: Cable },
  { id: "lubricants", title: "Lubricants", desc: "Engine oils for petrol, diesel & two-wheeler engines.", img: IMG.lubricants, icon: Droplet },
  { id: "solar", title: "Solar Product Solution", desc: "Solar batteries for homes, farms and businesses.", img: IMG.solar, icon: Sun, span: "md:col-span-2" },
];

const WHY = [
  { icon: ShieldCheck, title: "Genuine Products", text: "100% original Amaron product line with proper billing." },
  { icon: Sparkles, title: "Expert Product Selection", text: "Trained team helps you find the right fit — no guesswork." },
  { icon: Truck, title: "Fast Delivery Support", text: "Doorstep delivery across Sikar and nearby areas." },
  { icon: Wrench, title: "Installation Guidance", text: "Correct installation, no shortcuts — safer batteries." },
  { icon: ShieldCheck, title: "Warranty Assistance", text: "End-to-end warranty claim support handled by us." },
  { icon: Zap, title: "Batteries to Lubricants", text: "One stop for batteries, HUPS, lubricants & solar." },
];

export default function HomePage() {
  const [selected, setSelected] = useState(null);

  return (
    <div className="bg-background">
      {/* HERO */}
      <section className="relative hero-bg overflow-hidden">
        <div className="grain absolute inset-0" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-16 md:pt-24 pb-14 md:pb-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/10 text-secondary text-xs font-bold uppercase tracking-[0.2em]">
              <span className="h-1.5 w-1.5 rounded-full bg-primary pulse-dot" />
              Est. 1965 · Sikar's Most Trusted Battery Shop
            </div>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black leading-[1.02]">
              Reliable <span className="text-secondary">Amaron</span> Battery<br className="hidden sm:block" /> Solutions for Every Need.
            </h1>
            <p className="mt-5 text-base md:text-lg text-foreground/80 max-w-xl">
              Sikar's most trusted battery shop since 1965 (Shafi Battery Wala) — Amaron authorised since 1997. Two-Wheeler, Automotive, Inverter Battery, HUPS, Lubricants and Solar Power Solutions with expert guidance and doorstep support.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#selector" id="hero-cta-find">
                <Button data-testid="hero-cta-find" className="rounded-full bg-primary text-black hover:bg-secondary hover:text-white px-7 h-12 text-base font-semibold">
                  Find Your Battery <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </a>
              <a href="/find-battery/request">
                <Button data-testid="hero-cta-quote" variant="outline" className="rounded-full border-black/15 hover:bg-black/5 px-7 h-12 text-base font-semibold">
                  Request a Call Back
                </Button>
              </a>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg">
              <MiniStat label="Product Lines" value="4" />
              <MiniStat label="Years of Trust" value="60+" />
              <MiniStat label="Happy Customers" value="1L+" />
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative rounded-3xl overflow-hidden border border-black/10 aspect-[4/5] bg-primary">
              <img src={IMG.hero} alt="Amaron products — Switch to savings, switch on power" className="absolute inset-0 h-full w-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:block">
              <Card className="bg-primary text-primary-foreground rounded-xl p-3 w-44 border border-black/10 shadow-2xl">
                <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest"><ShieldCheck className="h-3.5 w-3.5" /> Genuine Warranty</div>
                <div className="mt-1 text-base font-black leading-tight">Up to 72 months</div>
                <div className="text-[10px] opacity-80 mt-0.5">on selected models</div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORY BENTO */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.24em] text-secondary font-bold">Product Categories</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold mt-2">One brand. Every power need.</h2>
          </div>
          <Link to="/products" data-testid="link-see-all-products" className="text-sm font-semibold text-secondary hover:underline">See all categories →</Link>
        </div>

        <div className="grid md:grid-cols-4 md:auto-rows-[220px] gap-4 md:gap-6">
          {CATEGORIES.map(c => (
            <CategoryCard key={c.id} {...c} />
          ))}
        </div>
      </section>

      {/* SELECTOR */}
      <section id="selector" className="relative max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-16 scroll-mt-24">
        <BatterySelector onSelect={setSelected} />
      </section>

      {/* WHY US */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
        <div className="text-xs uppercase tracking-[0.24em] text-secondary font-bold">Why Choose Us</div>
        <h2 className="font-display text-3xl md:text-4xl font-bold mt-2 max-w-2xl">Bold products. Backed by boldly reliable service.</h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {WHY.map((w) => (
            <Card key={w.title} className="bg-card border-black/10 p-6 rounded-2xl hover:-translate-y-1 transition-transform duration-300">
              <div className="h-11 w-11 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center">
                <w.icon className="h-5 w-5 text-secondary" />
              </div>
              <div className="mt-4 font-display font-bold text-lg">{w.title}</div>
              <p className="text-sm text-muted-foreground mt-1.5">{w.text}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* ABOUT */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <div className="text-xs uppercase tracking-[0.24em] text-secondary font-bold">About Us</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold mt-2">Trusted battery partner in Sikar.</h2>
          <p className="mt-4 text-foreground/80 leading-relaxed">
            We are a trusted battery solutions provider offering Amaron automotive batteries, two wheeler batteries,
            inverter batteries, HUPS batteries and solar power solutions. Our goal is to help customers choose the right
            battery with reliable service, quick support and genuine products.
          </p>
          <ul className="mt-6 space-y-3">
            {["Trained battery consultants", "Genuine Amaron product line", "Doorstep delivery & installation guidance"].map((t) => (
              <li key={t} className="flex items-center gap-2 text-sm"><CheckCircle2 className="h-4 w-4 text-secondary" /> {t}</li>
            ))}
          </ul>
        </div>
        <div className="relative rounded-3xl overflow-hidden border border-black/10 aspect-[16/10]">
          <img src={IMG.storeInside} alt="Inside our Bharat Battery House store" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        </div>
      </section>

      {/* CONTACT + LEAD FORM */}
      <section id="contact" className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24 grid lg:grid-cols-5 gap-8 scroll-mt-24">
        <div className="lg:col-span-2 space-y-4">
          <div className="text-xs uppercase tracking-[0.24em] text-secondary font-bold">Reach Us</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold">Bharat Battery House</h2>
          <ContactRow icon={MapPin} title="Address" value={<>Near HP Petrol Pump,<br />Bajaj Road, Sikar (Raj.) 332001</>} />
          <ContactRow icon={Phone} title="Phone" value={<><a href="tel:+919829219117" className="hover:text-secondary">+91 98292 19117</a><br /><a href="tel:+919549390000" className="hover:text-secondary">+91 95493 90000</a><br /><a href="tel:+919530498888" className="hover:text-secondary">+91 95304 98888</a></>} />
          <ContactRow icon={Clock} title="Business Hours" value="Mon–Sat 10:00 am – 8:00 pm · Sunday Closed" />
          <div className="flex flex-wrap gap-3 pt-2">
            <a href="tel:+919549390000" data-testid="contact-call">
              <Button className="rounded-full bg-primary text-black hover:bg-secondary hover:text-white"><Phone className="h-4 w-4 mr-1.5" /> Call Now</Button>
            </a>
            <a href="https://wa.me/919530498888" target="_blank" rel="noreferrer" data-testid="contact-whatsapp">
              <Button variant="outline" className="rounded-full border-black/15"><MessageCircle className="h-4 w-4 mr-1.5" /> WhatsApp</Button>
            </a>
            <a href="https://www.google.com/maps?gs_lcrp=EgZjaHJvbWUqCAgCEEUYJxg7MgYIABBFGDwyBggBEEUYPDIICAIQRRgnGDsyBggDEEUYOTIICAQQRRgnGDsyBggFEEUYPDIGCAYQRRhBMgYIBxBFGEHSAQg0NzcxajBqN6gCCLACAfEFoNjPM30danU&um=1&ie=UTF-8&fb=1&gl=in&sa=X&geocode=KXcLJzbJpGw5MVvH0kg0rsGJ&daddr=Bajaj+Rd,+near+Sundaram+Petrol+Pump,+Subhash+Chowk,+Sikar,+Rajasthan+332001" target="_blank" rel="noreferrer" data-testid="contact-directions">
              <Button variant="outline" className="rounded-full border-black/15"><MapPin className="h-4 w-4 mr-1.5" /> Directions</Button>
            </a>
          </div>
        </div>
        <div className="lg:col-span-3">
          <LeadForm preselect={selected ? {
            product_category: selected.category,
            selected_brand: selected.brand_series,
            selected_model: selected.model_code,
          } : {}} />
        </div>
      </section>
    </div>
  );
}

function CategoryCard({ id, title, desc, img, icon: Icon, span }) {
  return (
    <Link to="/products" data-testid={`cat-${id}`} className={`group relative rounded-2xl overflow-hidden border border-black/10 ${span || ""} block min-h-[220px]`}>
      <img src={img} alt={title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/10" />
      <div className="relative h-full p-5 flex flex-col justify-between">
        <div className="h-10 w-10 rounded-lg bg-primary/20 border border-primary/40 flex items-center justify-center backdrop-blur">
          <Icon className="h-5 w-5 text-secondary" />
        </div>
        <div>
          <div className="font-display font-bold text-xl md:text-2xl">{title}</div>
          <p className="text-xs md:text-sm text-white/70 mt-1 max-w-xs">{desc}</p>
          <div className="mt-3 inline-flex items-center gap-1 text-secondary text-xs font-bold uppercase tracking-widest">
            Explore <ArrowRight className="h-3.5 w-3.5" />
          </div>
        </div>
      </div>
    </Link>
  );
}

function MiniStat({ label, value }) {
  return (
    <div>
      <div className="font-display text-2xl md:text-3xl font-black text-secondary">{value}</div>
      <div className="text-[11px] uppercase tracking-widest text-muted-foreground mt-1">{label}</div>
    </div>
  );
}

function ContactRow({ icon: Icon, title, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="h-9 w-9 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center flex-shrink-0">
        <Icon className="h-4 w-4 text-secondary" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-muted-foreground">{title}</div>
        <div className="mt-0.5 text-sm">{value}</div>
      </div>
    </div>
  );
}
